import React, { useMemo, useRef, useState } from "react";
import { Search, Minus, Plus } from "lucide-react";
import { Modal, Icon } from "./common";
import Avatar from "./Avatar";
import { useLang } from "../i18n";
import api, { apiError } from "../api";
import { toast } from "sonner";

export default function AwardPointsModal({
  open,
  onClose,
  students = [],
  categories = [],
  presetIds = [],
  onDone,
  action = "all",
}) {
  const { t, pick } = useLang();
  const [selected, setSelected] = useState(() => new Set(presetIds));
  const [search, setSearch] = useState("");
  const [catKey, setCatKey] = useState(null);
  const [amount, setAmount] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);
  const requestIdRef = useRef(null);

  const filtered = useMemo(() => students.filter((s) =>
    !search || (s.name_ar || "").includes(search) || (s.name_en || "").toLowerCase().includes(search.toLowerCase()) || (s.username || "").includes(search)
  ), [students, search]);

  const availableCategories = categories.filter((category) => {
    if (action === "add") return category.default_points > 0;
    if (action === "deduct") return category.default_points < 0;
    return true;
  });
  const cat = availableCategories.find((category) => category.key === catKey);
  const finalAmount = amount ?? (cat ? cat.default_points : 0);
  const isNeg = !!(cat && cat.default_points < 0);
  const fmtPts = (v) => (v > 0 ? `+${v}` : `${v}`);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const reset = () => {
    setSelected(new Set(presetIds)); setSearch(""); setCatKey(null);
    setAmount(null); setReason(""); setNotes(""); setConfirming(false);
    submittingRef.current = false;
    requestIdRef.current = null;
  };

  const submit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      requestIdRef.current = requestIdRef.current || crypto.randomUUID();
      const { data } = await api.post("/staff/points/award", {
        student_ids: [...selected], category_key: catKey,
        amount: isNeg ? null : finalAmount, reason: reason || pick(cat, "name"), notes,
        client_request_id: requestIdRef.current,
      });
      const undoable = data.results.filter((r) => r.tx_id && !r.duplicate);
      const successMessage = selected.size > 1
        ? isNeg
          ? t("teacher.bulk_deduct_done", { n: selected.size, amount: Math.abs(data.amount) })
          : t("teacher.bulk_award_done", { n: selected.size, amount: data.amount })
        : isNeg
          ? t("teacher.deducted_ok", { n: Math.abs(data.amount) })
          : t("teacher.awarded_ok", { n: data.amount });
      toast.success(successMessage, {
        action: undoable.length ? {
          label: t("common.undo"),
          onClick: async () => {
            for (const r of undoable) await api.post(`/staff/points/${r.tx_id}/undo`).catch(() => {});
            toast.success(t("teacher.undone_ok"));
            onDone?.();
          },
        } : undefined,
        duration: 8000,
      });
      const leveled = data.results.find((r) => r.leveled_up);
      if (leveled) toast.success(`👑 ${leveled.name_ar}: ${pick(leveled.leveled_up, "name")}`, { duration: 6000 });
      reset();
      onClose();
      onDone?.(data.results);
    } catch (e) {
      toast.error(apiError(e, t));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }}
      title={action === "deduct" ? t("teacher.deduct_points") : t("teacher.add_points")}
      testId="award-points-modal" wide>
      {!confirming ? (
        <div className="space-y-5">
          <div>
            <p className="text-sm font-extrabold mb-2 flex items-center justify-between">
              {t("teacher.select_students")}
              <span data-testid="selected-count" className="text-[#27C9EE]">{t("teacher.selected_count", { n: selected.size })}</span>
            </p>
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-[#A5A0D6]" />
              <input data-testid="award-search-input" className="input-magic ps-9" placeholder={t("teacher.search_student")}
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="max-h-44 overflow-y-auto space-y-1.5 pe-1">
              {filtered.map((s) => (
                <button key={s.id} data-testid={`award-student-${s.username}`} onClick={() => toggle(s.id)}
                  className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 border transition-colors ${selected.has(s.id) ? "bg-[#7625D8]/25 border-[#9C42F5]/60" : "bg-[#0D0B38]/50 border-white/8 hover:border-white/20"}`}>
                  <Avatar src={s.avatar} name={pick(s, "name")} size="xs" />
                  <span className="flex-1 text-start text-sm font-bold">{pick(s, "name")}</span>
                  <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center text-[10px] font-black ${selected.has(s.id) ? "bg-[#9C42F5] border-[#9C42F5]" : "border-white/25"}`}>
                    {selected.has(s.id) ? "✓" : ""}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-extrabold mb-2">{t("teacher.category")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {availableCategories.map((c) => (
                <button key={c.key} data-testid={`award-category-${c.key}`}
                  onClick={() => { setCatKey(c.key); setAmount(null); setReason((r) => r || pick(c, "name")); }}
                  className={`rounded-xl p-3 border text-center transition-all ${catKey === c.key ? "scale-[1.03]" : "opacity-75 hover:opacity-100"}`}
                  style={{
                    background: `linear-gradient(165deg, ${c.color}${catKey === c.key ? "40" : "18"}, ${c.color}08)`,
                    borderColor: `${c.color}${catKey === c.key ? "CC" : "44"}`,
                    boxShadow: catKey === c.key ? `0 0 16px ${c.color}55` : "none",
                  }}>
                  <Icon name={c.icon} className="w-5 h-5 mx-auto mb-1" style={{ color: c.color }} />
                  <p className="text-[11px] font-extrabold leading-tight">{pick(c, "name")}</p>
                  <p className="text-sm font-black" style={{ color: c.color }} dir="ltr">{fmtPts(c.default_points)}</p>
                </button>
              ))}
            </div>
          </div>
          {cat && !isNeg && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-sm font-extrabold mb-1.5">{t("dash.points_label")}</p>
                <div className="flex items-center gap-2">
                  <button data-testid="amount-minus" onClick={() => setAmount(Math.max(1, finalAmount - 1))} className="w-9 h-9 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Minus className="w-4 h-4" /></button>
                  <span data-testid="award-amount" className="flex-1 text-center text-2xl font-black text-[#48D276]">+{finalAmount}</span>
                  <button data-testid="amount-plus" onClick={() => setAmount(Math.min(100, finalAmount + 1))} className="w-9 h-9 rounded-xl bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
              <div>
                <p className="text-sm font-extrabold mb-1.5">{t("teacher.reason")}</p>
                <input data-testid="award-reason-input" className="input-magic" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={pick(cat, "name")} />
              </div>
              <div className="sm:col-span-2">
                <p className="text-sm font-extrabold mb-1.5">{t("teacher.notes")} <span className="text-[#A5A0D6] font-semibold">({t("common.optional")})</span></p>
                <input data-testid="award-notes-input" className="input-magic" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
          )}
          {cat && isNeg && (
            <div className="rounded-2xl bg-[#F43F5E]/10 border border-[#F43F5E]/30 p-4 text-center">
              <p data-testid="award-amount-neg" className="text-2xl font-black text-[#FF8FB1]" dir="ltr">{finalAmount}</p>
              <p className="text-xs font-bold text-[#A5A0D6] mt-1">{pick(cat, "name")}</p>
              <input data-testid="award-reason-input" className="input-magic mt-3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("teacher.reason")} />
            </div>
          )}
          <button data-testid="award-preview-btn" disabled={selected.size === 0 || !catKey}
            onClick={() => setConfirming(true)} className="btn-magic w-full">
            {t("common.confirm")} ←
          </button>
        </div>
      ) : (
        <div className="space-y-5 text-center">
          <h4 data-testid="award-confirm-title" className={`text-2xl font-black ${isNeg ? "text-[#FF8FB1]" : "text-[#48D276]"}`}>
            {isNeg ? t("teacher.confirm_deduct", { n: Math.abs(finalAmount) }) : t("teacher.confirm_award", { n: finalAmount })}
          </h4>
          {selected.size > 1 && (
            <p data-testid="bulk-confirmation-message" className="font-extrabold text-[#D8D4F2]">
              {isNeg
                ? t("teacher.bulk_confirm_deduct", { n: selected.size, amount: Math.abs(finalAmount) })
                : t("teacher.bulk_confirm_award", { n: selected.size, amount: finalAmount })}
            </p>
          )}
          <div className="rounded-2xl bg-[#0D0B38]/60 border border-white/10 p-4 space-y-2 text-sm font-bold">
            <p><span className="text-[#A5A0D6]">{t("teacher.student_label")}: </span>
              {students.filter((s) => selected.has(s.id)).map((s) => pick(s, "name")).join("، ")}
            </p>
            <p><span className="text-[#A5A0D6]">{t("teacher.reason")}: </span>{reason || pick(cat, "name")}</p>
            <p><span className="text-[#A5A0D6]">{t("teacher.category")}: </span>{pick(cat, "name")}</p>
            <p data-testid="award-confirm-value">
              <span className="text-[#A5A0D6]">{t("teacher.category_value")}: </span>
              <span dir="ltr">{fmtPts(finalAmount)}</span>
            </p>
          </div>
          <p className="font-extrabold text-[#D8D4F2]">{t("common.are_you_sure")}</p>
          <div className="flex gap-3">
            <button data-testid="award-cancel-btn" onClick={() => setConfirming(false)} className="btn-magic btn-ghost flex-1">{t("common.cancel")}</button>
            <button data-testid="award-confirm-btn" disabled={busy} onClick={submit} className="btn-magic flex-1">
              {busy ? t("common.loading") : t("common.confirm")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
